import { useMemo } from "react";
import {
  LayoutDashboard, ReceiptText, Package, Users, Truck, ShoppingCart, WalletCards,
  ChartNoAxesCombined, FileStack, FileMinus2, Plus, ArrowRight,
} from "lucide-react";
import { money } from "../lib/api";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandSeparator,
} from "../components/ui/command";

const SECTIONS = [
  { id: "dashboard", label: "Overview", icon: LayoutDashboard },
  { id: "invoices", label: "Invoices", icon: ReceiptText },
  { id: "sales_orders", label: "Sales Orders", icon: FileStack },
  { id: "credit_notes", label: "Credit Notes", icon: FileMinus2 },
  { id: "inventory", label: "Inventory", icon: Package },
  { id: "customers", label: "Customers", icon: Users },
  { id: "suppliers", label: "Suppliers", icon: Truck },
  { id: "purchases", label: "Purchases", icon: ShoppingCart },
  { id: "expenses", label: "Expenses", icon: WalletCards },
  { id: "reports", label: "Reports", icon: ChartNoAxesCombined },
];

const QUICK_ACTIONS = [
  { id: "invoice", label: "Create invoice", icon: ReceiptText },
  { id: "sales_order", label: "Create sales order", icon: FileStack },
  { id: "customer", label: "Add customer", icon: Users },
  { id: "product", label: "Add product", icon: Package },
  { id: "supplier", label: "Add supplier", icon: Truck },
  { id: "purchase", label: "Create purchase order", icon: ShoppingCart },
  { id: "expense", label: "Record expense", icon: WalletCards },
];

export default function CommandPalette({ open, onOpenChange, data, currency, onNavigate, onCreate, onOpenInvoice }) {
  const results = useMemo(() => {
    if (!data) return { customers: [], invoices: [], products: [], sales_orders: [], suppliers: [], credit_notes: [] };
    return {
      customers: (data.customers || []).slice(0, 6),
      invoices: (data.invoices || []).slice(0, 6),
      products: (data.products || []).slice(0, 6),
      sales_orders: (data.sales_orders || []).slice(0, 6),
      suppliers: (data.suppliers || []).slice(0, 6),
      credit_notes: (data.credit_notes || []).slice(0, 6),
    };
  }, [data]);

  const go = (fn) => { onOpenChange(false); fn(); };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Search or jump to… (customers, invoices, products, actions)" data-testid="command-palette-input" />
      <CommandList data-testid="command-palette-list">
        <CommandEmpty>No results found.</CommandEmpty>

        <CommandGroup heading="Quick actions">
          {QUICK_ACTIONS.map((action) => (
            <CommandItem key={action.id} value={`action ${action.label}`} onSelect={() => go(() => onCreate(action.id))} data-testid={`command-action-${action.id}`}>
              <Plus size={16} /> {action.label}
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />
        <CommandGroup heading="Go to">
          {SECTIONS.map((section) => {
            const Icon = section.icon;
            return (
              <CommandItem key={section.id} value={`goto ${section.label}`} onSelect={() => go(() => onNavigate(section.id))} data-testid={`command-goto-${section.id}`}>
                <Icon size={16} /> {section.label} <ArrowRight size={13} style={{ marginLeft: "auto", opacity: 0.5 }} />
              </CommandItem>
            );
          })}
        </CommandGroup>

        {results.customers.length > 0 && (
          <CommandGroup heading="Customers">
            {results.customers.map((c) => (
              <CommandItem key={c.id} value={`customer ${c.name} ${c.company || ""} ${c.email || ""}`} onSelect={() => go(() => onNavigate("customers"))} data-testid={`command-customer-${c.id}`}>
                <Users size={16} /> {c.name}{c.company ? ` · ${c.company}` : ""}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {results.invoices.length > 0 && (
          <CommandGroup heading="Invoices">
            {results.invoices.map((inv) => (
              <CommandItem key={inv.id} value={`invoice ${inv.number} ${inv.customer_name} ${inv.status}`} onSelect={() => go(() => onOpenInvoice(inv))} data-testid={`command-invoice-${inv.id}`}>
                <ReceiptText size={16} /> {inv.number} · {inv.customer_name} <span style={{ marginLeft: "auto", opacity: 0.6 }}>{money(inv.total, currency)}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {results.products.length > 0 && (
          <CommandGroup heading="Products">
            {results.products.map((p) => (
              <CommandItem key={p.id} value={`product ${p.name} ${p.sku || ""} ${p.category || ""}`} onSelect={() => go(() => onNavigate("inventory"))} data-testid={`command-product-${p.id}`}>
                <Package size={16} /> {p.name}{p.sku ? ` · ${p.sku}` : ""} <span style={{ marginLeft: "auto", opacity: 0.6 }}>{p.stock} in stock</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {results.sales_orders.length > 0 && (
          <CommandGroup heading="Sales Orders">
            {results.sales_orders.map((so) => (
              <CommandItem key={so.id} value={`sales order ${so.number} ${so.customer_name} ${so.status}`} onSelect={() => go(() => onNavigate("sales_orders"))} data-testid={`command-so-${so.id}`}>
                <FileStack size={16} /> {so.number} · {so.customer_name} <span style={{ marginLeft: "auto", opacity: 0.6 }}>{so.status}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {results.suppliers.length > 0 && (
          <CommandGroup heading="Suppliers">
            {results.suppliers.map((s) => (
              <CommandItem key={s.id} value={`supplier ${s.name} ${s.company || ""}`} onSelect={() => go(() => onNavigate("suppliers"))} data-testid={`command-supplier-${s.id}`}>
                <Truck size={16} /> {s.name}{s.company ? ` · ${s.company}` : ""}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {results.credit_notes.length > 0 && (
          <CommandGroup heading="Credit Notes">
            {results.credit_notes.map((cn) => (
              <CommandItem key={cn.id} value={`credit note ${cn.number} ${cn.customer_name}`} onSelect={() => go(() => onNavigate("credit_notes"))} data-testid={`command-cn-${cn.id}`}>
                <FileMinus2 size={16} /> {cn.number} · {cn.customer_name} <span style={{ marginLeft: "auto", opacity: 0.6 }}>{money(cn.total, currency)}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}
